import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { TOAST_MS } from "../lib/constants";
import { useAllShows } from "../hooks/useShows";
import Poster from "../components/Poster";
import { createList, getMyLists, getList } from "../lib/services";
import { formatApiError } from "../lib/api";

export default function Lists() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const allShows = useAllShows();
  const findShow = (id) => allShows.find((s) => s.id === id);

  const [lists, setLists] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // for poster collage on each list card we need to fetch the first show
  // ids per list. To avoid N+1 calls, we accept that in the alpha some lists
  // show "no posters yet" until the user opens them.
  // (server's GET /me/lists returns itemCount but not showIds. Acceptable.)
  const [listPosters, setListPosters] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { items } = await getMyLists();
      setLists(items || []);
      // optional: lazy-load posters for each non-empty list
      const nonEmpty = (items || []).filter((l) => l.itemCount > 0);
      const detailEntries = await Promise.all(
        nonEmpty.slice(0, 8).map(async (l) => {
          try {
            const d = await getList(l.id);
            return [l.id, (d.shows || []).slice(0, 4).map((x) => x.show)];
          } catch {
            return [l.id, []];
          }
        })
      );
      setListPosters(Object.fromEntries(detailEntries));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // auto-open form when arriving via /lists?create=1
  useEffect(() => {
    if (params.get("create") === "1") {
      setShowForm(true);
      const next = new URLSearchParams(params);
      next.delete("create");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast("title required");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createList({
        title: title.trim(),
        description: description.trim(),
        visibility: "public",
      });
      toast("list created", { duration: TOAST_MS.FAST });
      setTitle("");
      setDescription("");
      setShowForm(false);
      navigate(`/list/${created.id}`);
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="lists-page" className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
          lists
        </div>
        <h1 className="font-display text-5xl leading-none mt-1">
          lists<span className="text-acid">.</span>
        </h1>
        <p className="font-mono text-[11px] text-white/50 mt-2">
          group shows however you want.
        </p>
      </header>

      <section className="px-5 py-5 border-b-2 border-white/15">
        {!showForm ? (
          <button
            data-testid="create-list-btn"
            onClick={() => setShowForm(true)}
            className="w-full font-mono text-xs lowercase border-2 border-acid text-acid py-3 hover:bg-acid hover:text-black transition-colors flex items-center justify-center gap-1.5"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            create list
          </button>
        ) : (
          <form onSubmit={create} data-testid="create-list-form" className="space-y-3 border-2 border-acid p-4 bg-acid/5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                title
              </label>
              <input
                data-testid="create-list-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                placeholder="e.g. closing soon, must see"
                className="mt-1 w-full bg-transparent border-2 border-white/30 px-3 py-2.5 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                description (optional)
              </label>
              <textarea
                data-testid="create-list-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="what's the through-line?"
                className="mt-1 w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                data-testid="create-list-submit"
                disabled={submitting}
                className="flex-1 bg-acid text-black font-mono text-sm uppercase tracking-wider py-2.5 border-2 border-acid hover:bg-black hover:text-acid transition-colors disabled:opacity-50"
              >
                {submitting ? "creating..." : "create list"}
              </button>
              <button
                type="button"
                data-testid="create-list-cancel"
                onClick={() => { setShowForm(false); setTitle(""); setDescription(""); }}
                className="font-mono text-xs uppercase tracking-wider text-white/60 hover:text-white px-3 py-2.5 inline-flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" /> cancel
              </button>
            </div>
          </form>
        )}
      </section>

      {!loaded ? (
        <div className="px-5 py-10 text-center font-mono text-xs text-white/40">loading...</div>
      ) : lists.length === 0 ? (
        <div className="px-5 py-16 text-center">
          <p className="font-display text-2xl text-white/70 leading-tight">
            no lists yet.
          </p>
          <p className="font-mono text-xs text-white/40 mt-3">
            create one above.
          </p>
        </div>
      ) : (
        <ul>
          {lists.map((l, i) => {
            const posters = listPosters[l.id] || [];
            return (
              <li key={l.id} data-testid={`list-item-${l.id}`}>
                <Link
                  to={`/list/${l.id}`}
                  className="block border-b-2 border-white/15 px-5 py-5 hover:bg-acid/5 transition-colors"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {String(i + 1).padStart(2, "0")} · {l.itemCount} {l.itemCount === 1 ? "show" : "shows"}
                  </div>
                  <div className="font-display text-2xl leading-tight mt-1">
                    {(l.title || "untitled list").toLowerCase()}
                  </div>
                  {l.description && (
                    <div className="font-mono text-[11px] text-white/50 mt-1 leading-relaxed">
                      {l.description}
                    </div>
                  )}
                  <div
                    data-testid={`list-poster-row-${l.id}`}
                    className="mt-3 flex items-center gap-2"
                  >
                    {posters.length === 0 ? (
                      <div className="font-mono text-[10px] uppercase tracking-widest text-white/30 border border-dashed border-white/20 px-2 py-2">
                        {l.itemCount > 0 ? "open to view" : "no posters yet"}
                      </div>
                    ) : (
                      posters.map((s) => <Poster key={s.id} show={s} size="sm" />)
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <div className="px-5 py-6 text-center">
        <Link to="/toronto" className="font-mono text-[11px] uppercase tracking-wider text-acid underline underline-offset-4">
          back to toronto
        </Link>
      </div>
    </main>
  );
}
